import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { InitialData } from 'app/app.types';
import { AuthUtils } from 'app/authentication/auth/auth.utils';
import { environment } from 'environments/environment';
import { buildModuleNavigationItems, UI_PREVIEW_MODULE_CODES } from 'app/shared/entitlement/module-nav.config';
import { resolveMockApiConfig } from 'app/mocks/mock-api.config';
import { isExternalStoreHost, isServicesBoHost } from 'app/modules/services-store/services-host.util';

@Injectable({
    providedIn: 'root'
})
export class InitialDataResolver {
    private readonly _resolverTimeoutMs = 12000;
    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Use this resolver to resolve initial mock-api for the application
     *
     * @param route
     * @param state
     */
    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<InitialData> {
        // Fork join multiple API endpoint calls to wait all of them to finish
        const messages$ = (isServicesBoHost() || isExternalStoreHost()) ? of([]) : this._safeGet('api/common/messages', []);

        return forkJoin([
            messages$,
            this._safeGet('api/common/navigation', {}),
            this._safeGet('api/common/notifications', []),
            this._safeGet('api/common/shortcuts', [])
        ]).pipe(
            map(([messages, navigation, notifications, shortcuts]) => {
                const resolvedNavigation = (navigation || {}) as any;
                const user = this._readStoredUser();
                const sessionMenu = this._buildNavigationByRole(user?.role, user);

                return {
                    messages,
                    navigation: {
                        compact: sessionMenu.compact || resolvedNavigation.compact || [],
                        default: sessionMenu.default || resolvedNavigation.default || [],
                        futuristic: sessionMenu.futuristic || resolvedNavigation.futuristic || [],
                        horizontal: sessionMenu.horizontal || resolvedNavigation.horizontal || []
                    },
                    notifications,
                    shortcuts,
                    user
                };
            }
            ),
            catchError((error) => {
                console.error('[InitialDataResolver] Failed to resolve initial data after sign-in.', {
                    route: state.url,
                    error
                });

                return of({
                    messages: [],
                    navigation: {
                        compact: [],
                        default: [],
                        futuristic: [],
                        horizontal: []
                    },
                    notifications: [],
                    shortcuts: [],
                    user: null
                } as unknown as InitialData);
            })
        );
    }

    private _safeGet<T>(url: string, fallback: T): Observable<T> {
        return this._httpClient.get<T>(url).pipe(
            timeout(this._resolverTimeoutMs),
            catchError((error) => {
                console.error('[InitialDataResolver] GET failed, using fallback.', {
                    url,
                    error
                });
                return of(fallback);
            })
        );
    }

    private _readStoredUser(): any {
        return this._normalizeAuthenticationUser(environment.user?.profile ?? null);
    }

    private _normalizeAuthenticationUser(user: any): any {
        if (!user) {
            return null;
        }

        return {
            ...user,
            role: this._resolveRoleValue(user)
        };
    }

    private _resolveRoleValue(user: any): string[] {
        return AuthUtils.resolveEffectiveRoles(user?.extra?.role_map || user?.role, { user });
    }

    private _buildNavigationByRole(role: any, user?: any): any {
        const isSuperAdmin = environment.user?.superadmin === true;
        const isAdmin = false;// environment.user?.roles.admin === true;

        const superAdminMenu: any[] = [];

        const adminMenu = [
            { id: 'dashboard', title: 'Dashboard', type: 'basic', icon: 'heroicons_outline:home', link: '/dashboard' },
            { id: 'organizations', title: 'Organization', type: 'basic', icon: 'heroicons_outline:office-building', link: '/admin/organizations' },
            { id: 'branches', title: 'Branches', type: 'basic', icon: 'heroicons_outline:collection', link: '/admin/branches' },
            { id: 'users', title: 'Users', type: 'basic', icon: 'heroicons_outline:users', link: '/admin/users' }
        ];

        const userMenu = [
            { id: 'dashboard', title: 'Dashboard', type: 'basic', icon: 'heroicons_outline:home', link: '/dashboard' },
        ];

        const branchModules = (environment.selection?.branch as { modules?: string[] } | undefined)?.modules;
        const mockNav = resolveMockApiConfig(environment.production, environment.mockApi).enabled;
        const moduleCodesForNav = isSuperAdmin
            ? UI_PREVIEW_MODULE_CODES
            : isServicesBoHost()
                ? [...new Set(['store', 'website', ...(branchModules ?? [])])]
                : mockNav
                    ? [...new Set([...(branchModules ?? []), ...UI_PREVIEW_MODULE_CODES])]
                    : branchModules;
        const moduleNav = buildModuleNavigationItems(moduleCodesForNav);

        if (isServicesBoHost()) {
            const menu = this._buildBoNavigationMenu(moduleNav);
            return {
                compact: menu,
                default: menu,
                futuristic: menu,
                horizontal: menu
            };
        }

        const insertAt = (items: any[], afterId: string, toInsert: any[]) => {
            const idx = items.findIndex((i) => i.id === afterId);
            if (idx < 0) {
                return [...items, ...toInsert];
            }
            return [...items.slice(0, idx + 1), ...toInsert, ...items.slice(idx + 1)];
        };

        let menu = isSuperAdmin
            ? superAdminMenu
            : isAdmin
                ? adminMenu
                : userMenu;
        menu = insertAt(menu, 'dashboard', moduleNav);

        return {
            compact: menu,
            default: menu,
            futuristic: menu,
            horizontal: menu
        };
    }

    /** Back Office sidebar — Dashboard + dividers between module groups (matches CC chrome). */
    private _buildBoNavigationMenu(moduleNav: any[]): any[] {
        const menu: any[] = [
            {
                id: 'dashboard',
                title: 'Dashboard',
                type: 'basic',
                icon: 'heroicons_outline:chart-pie',
                link: '/dashboard'
            },
            { id: 'nav-divider-main', type: 'divider' }
        ];

        moduleNav.forEach((item, index) => {
            if (index > 0) {
                menu.push({ id: `nav-divider-${item.id}`, type: 'divider' });
            }
            menu.push(item);
        });

        return menu;
    }
}
